// WertungJeDisziplin.jsx — Wertung je Disziplin für EIN Turnier (V19, 18.10.2026)
//
// Bis heute standen die Wertungsangaben eines Turniers an zwei Stellen:
// die Modi (Kampf, Formen, Bruchtest) in „Turnier bearbeiten → Einstellungen“
// und die Überschreibungen je Disziplin (K.O. oder Punkte, Punktebereiche,
// Kampfrichter; turnier_wertungs_einstellungen) auf der eigenen Seite
// „Turnier-Einstellungen → Turnier-spezifisch“. Jetzt stehen beide im Reiter
// „Einstellungen“ des Turniers. Die Vorgaben für alle Turniere bleiben auf der
// Seite „Turnier-Einstellungen“ (Plattform-Admin).
import React, { useState, useEffect, useCallback } from 'react';
import { regelFuer } from '../../lib/minuspunkte';
import { wertungFeldWirkt } from '../../lib/einstellungenWirkung';

// Punktebereich und Kampfrichterzahl wirken nur bei Formen (neue Kategorien,
// backend/utils/formenVorgaben.js); bei Selbstverteidigung, Bruchtest und den
// Kampfdisziplinen stehen sie deshalb nicht da (lib/einstellungenWirkung.js,
// 25.10.2026). Gespeicherte Werte gehen beim Speichern unverändert mit.
const mitPunkten = (typ) => wertungFeldWirkt('punkte_min_farbgurt', typ);
const mitKampfrichtern = (typ) => wertungFeldWirkt('anzahl_kampfrichter', typ);

// Automatische Disqualifikation (21.10.2026): im Turnier leer = aus (wird als 0
// gespeichert, damit es auch gegen eine andere Vorgabe gilt).
const mitAutoDq = (typ) => regelFuer({ divisionType: typ }).vonHand;
const autoDqText = (n) => (Number(n) > 0 ? `automatische Disqualifikation ab ${n}. Verwarnung` : 'keine automatische Disqualifikation');

const istPunkte = (s) => s && s !== 'bracket';

export default function WertungJeDisziplin({ turnierId, authHeader }) {
  const [zeilen, setZeilen] = useState([]);
  const [laedt, setLaedt] = useState(true);
  const [bearbeite, setBearbeite] = useState(null);
  const [entwurf, setEntwurf] = useState({});
  const [speichert, setSpeichert] = useState(false);
  const [meldung, setMeldung] = useState(null); // { art: 'ok'|'err', text }

  const laden = useCallback(async () => {
    if (!turnierId) return;
    setLaedt(true);
    try {
      const res = await fetch(`/api/wertung/turnier/${turnierId}`, { headers: authHeader });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) throw new Error(d.message || `Fehler ${res.status}`);
      setZeilen(d.data || []);
    } catch (e) {
      setMeldung({ art: 'err', text: `Wertung je Disziplin nicht geladen: ${e.message}` });
    } finally {
      setLaedt(false);
    }
  }, [turnierId, authHeader]);

  useEffect(() => { laden(); }, [laden]);

  const starten = (z) => {
    setMeldung(null);
    setBearbeite(z.division_type);
    setEntwurf({ ...z, wertungs_system: istPunkte(z.wertungs_system) ? 'pool' : 'bracket',
      auto_dq_ab_verwarnung: Number(z.auto_dq_ab_verwarnung) > 0 ? Number(z.auto_dq_ab_verwarnung) : '' });
  };

  const speichern = async () => {
    setSpeichert(true);
    try {
      const res = await fetch(`/api/wertung/turnier/${turnierId}/${encodeURIComponent(bearbeite)}`, {
        method: 'PUT', headers: authHeader,
        body: JSON.stringify({
          ...Object.fromEntries(Object.entries(entwurf).map(([k, v]) => [k, v === '' || Number.isNaN(v) ? null : v])),
          auto_dq_ab_verwarnung: mitAutoDq(bearbeite) && Number(entwurf.auto_dq_ab_verwarnung) > 0 ? Number(entwurf.auto_dq_ab_verwarnung) : 0,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) throw new Error(d.message || d.error || `Fehler ${res.status}`);
      setMeldung({ art: 'ok', text: `${bearbeite}: für dieses Turnier gespeichert.` });
      setBearbeite(null);
      laden();
    } catch (e) {
      setMeldung({ art: 'err', text: `Nicht gespeichert: ${e.message}` });
    } finally {
      setSpeichert(false);
    }
  };

  const zuruecksetzen = async (typ) => {
    if (!window.confirm(`„${typ}“ wieder nach der Vorgabe für alle Turniere werten?`)) return;
    try {
      const res = await fetch(`/api/wertung/turnier/${turnierId}/${encodeURIComponent(typ)}`, {
        method: 'DELETE', headers: authHeader,
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) throw new Error(d.message || `Fehler ${res.status}`);
      setMeldung({ art: 'ok', text: `${typ}: wieder nach Vorgabe.` });
      laden();
    } catch (e) {
      setMeldung({ art: 'err', text: `Nicht zurückgesetzt: ${e.message}` });
    }
  };

  const feld = (k, v) => setEntwurf((e) => ({ ...e, [k]: v }));
  const zahlFeld = (k, ganz = false) => (e) => feld(k, e.target.value === '' ? '' : (ganz ? parseInt(e.target.value, 10) : parseFloat(e.target.value)));

  return (
    <div className="tbe-card" style={{ marginTop: '1rem' }}>
      <div className="tbe-card-title">Wertung je Disziplin</div>
      <p className="tbe-hint" style={{ marginTop: 0 }}>
        K.O.-System oder Punktewertung, Punktebereiche, Zahl der Kampfrichter und automatische Disqualifikation für dieses Turnier.
        Ohne Anpassung gilt die Vorgabe für alle Turniere (Seite „Turnier-Einstellungen“).
      </p>
      {meldung && <div className={`tbe-msg ${meldung.art === 'ok' ? 'tbe-msg--ok' : 'tbe-msg--err'}`}>{meldung.text}</div>}
      {laedt ? (
        <div className="tbe-hint">Lade…</div>
      ) : (
        <div className="wjd-liste">
          {zeilen.map((z) => (
            <div key={z.division_type} className={`wjd-zeile${Number(z.ist_ueberschrieben) ? ' wjd-zeile--angepasst' : ''}`}>
              {bearbeite === z.division_type ? (
                <div className="wjd-form">
                  <strong>{z.division_type}</strong>
                  <div className="tbe-grid tbe-grid--2">
                    <div className="tbe-field">
                      <label>Wertung</label>
                      <select value={entwurf.wertungs_system} onChange={(e) => feld('wertungs_system', e.target.value)}>
                        <option value="bracket">K.O.-System (Brackets)</option>
                        <option value="pool">Punktewertung</option>
                      </select>
                    </div>
                    {mitKampfrichtern(z.division_type) && (
                      <div className="tbe-field">
                        <label>Kampfrichter (neue Kategorien)</label>
                        <input type="number" min="1" max="9" value={entwurf.anzahl_kampfrichter ?? ''} onChange={zahlFeld('anzahl_kampfrichter', true)} />
                      </div>
                    )}
                    {mitAutoDq(z.division_type) && (
                      <div className="tbe-field">
                        <label>Automatische Disqualifikation ab Verwarnung Nr. (leer = aus)</label>
                        <input type="number" min="1" max="20" value={entwurf.auto_dq_ab_verwarnung ?? ''} onChange={zahlFeld('auto_dq_ab_verwarnung', true)} />
                      </div>
                    )}
                    {istPunkte(entwurf.wertungs_system) && mitPunkten(z.division_type) && (
                      <>
                        <div className="tbe-field">
                          <label>Punkte Farbgurte (von bis)</label>
                          <div className="wjd-bereich">
                            <input type="number" step="0.5" value={entwurf.punkte_min_farbgurt ?? ''} onChange={zahlFeld('punkte_min_farbgurt')} aria-label="Farbgurte von" />
                            <span>bis</span>
                            <input type="number" step="0.5" value={entwurf.punkte_max_farbgurt ?? ''} onChange={zahlFeld('punkte_max_farbgurt')} aria-label="Farbgurte bis" />
                          </div>
                        </div>
                        <div className="tbe-field">
                          <label>Punkte Schwarzgurte (von bis)</label>
                          <div className="wjd-bereich">
                            <input type="number" step="0.5" value={entwurf.punkte_min_schwarzgurt ?? ''} onChange={zahlFeld('punkte_min_schwarzgurt')} aria-label="Schwarzgurte von" />
                            <span>bis</span>
                            <input type="number" step="0.5" value={entwurf.punkte_max_schwarzgurt ?? ''} onChange={zahlFeld('punkte_max_schwarzgurt')} aria-label="Schwarzgurte bis" />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="wjd-knoepfe">
                    <button type="button" className="tbe-btn-cancel" onClick={() => setBearbeite(null)} disabled={speichert}>Abbrechen</button>
                    <button type="button" className="tbe-btn-primary" onClick={speichern} disabled={speichert}>
                      {speichert ? 'Speichern…' : 'Speichern'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="wjd-text">
                    <strong>{z.division_type}</strong>
                    <span className="wjd-art">{istPunkte(z.wertungs_system) ? 'Punkte' : 'K.O.'}</span>
                    {istPunkte(z.wertungs_system) && mitPunkten(z.division_type) && (
                      <span className="tbe-hint">
                        Farbgurte {z.punkte_min_farbgurt}–{z.punkte_max_farbgurt} · Schwarzgurte {z.punkte_min_schwarzgurt}–{z.punkte_max_schwarzgurt}{mitKampfrichtern(z.division_type) ? ` · ${z.anzahl_kampfrichter} Kampfrichter` : ''}
                      </span>
                    )}
                    {mitAutoDq(z.division_type) && <span className="tbe-hint">{autoDqText(z.auto_dq_ab_verwarnung)}</span>}
                    {!!Number(z.ist_ueberschrieben) && <span className="wjd-angepasst">angepasst</span>}
                  </div>
                  <div className="wjd-knoepfe">
                    <button type="button" className="tbe-btn-cancel" onClick={() => starten(z)}>Anpassen</button>
                    {!!Number(z.ist_ueberschrieben) && (
                      <button type="button" className="tbe-btn-cancel" onClick={() => zuruecksetzen(z.division_type)}>Vorgabe</button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
